'use client'

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react' // Add useCallback
import { safeCountriesWhitelist } from '../helpers/config'
import { getOrCreateDeviceId, rememberSafeOnly } from '../helpers/helpers'
import { useKeepLanguage } from '../hooks/useKeepLanguage.ts'
import { safeGetItem } from '../helpers/safeStorage'
import {
  accountDocumentStatus,
  fetchAccountDocument,
} from '../lib/account-completion'
import * as accountsApi from '../lib/api/accounts.ts'
import { refusalWords } from '../lib/api/http.ts'
import Cookies from 'js-cookie'

const AuthContext = createContext('')

export const useAuth = () => {
  return useContext(AuthContext)
}

/**
 * Keep `<html data-pc-authed>` in step with the `pc_authed` cookie.
 *
 * An inline script in the root layout stamps this attribute before first paint so CSS can hide
 * logged-out-only marketing without a flash (see the `pc-anon-only` rule in src/index.css). That
 * script only runs on a full page load, and signing in does NOT reload — onAuthStateChanged just
 * sets the cookie — so without this the marketing would linger for the rest of the session after
 * a fresh sign-in. Signing out already reloads, but this keeps the two in sync either way.
 */
const syncAuthedAttribute = (authed) => {
  if (typeof document === 'undefined') return
  try {
    if (authed) {
      document.documentElement.setAttribute('data-pc-authed', '1')
    } else {
      document.documentElement.removeAttribute('data-pc-authed')
    }
  } catch {
    // Presentation only — never let this break auth.
  }
}

export const AuthProvider = ({ children }) => {
  const [, setVisitorId] = useState('')
  const [user, setUser] = useState(null)
  // v2 keeps the signed-in person's language, for the emails it sends them (ai_app_backend #1726); in the background.
  useKeepLanguage(user)
  const [loading, setLoading] = useState(true)
  // True once onAuthStateChanged has ACTUALLY fired. Distinct from `loading`, which the 8s
  // bootstrap timeout below can release while auth is still resolving — so `!loading && !user`
  // no longer means "definitively signed out". Redirect-to-signup effects must gate on
  // `authResolved && !user`, otherwise a slow (but successful) auth resolution gets a signed-in
  // user bounced off the page they opened.
  const [authResolved, setAuthResolved] = useState(false)
  // Optimistic auth hint: `true` if the `pc_authed` cookie is present (a returning, likely
  // logged-in user). Lets the header render the authed shell immediately instead of flashing
  // "Sign Up" while the async Firebase auth check resolves. Starts false to stay
  // hydration-safe (SSR/first client render), then reconciles on mount + on real auth events.
  const [authHint, setAuthHint] = useState(false)
  // Optimistic plan hint (plan NAME from the `pc_plan` cookie) so a returning PAID user sees
  // the paid header/badge immediately instead of the free-tier UI while subscription loads.
  // DISPLAY-ONLY — every capability/gate reads the real `subscription`/`gems`/`tier`. Starts
  // null (hydration-safe), reconciled on mount + on real subscription events.
  const [planHint, setPlanHint] = useState(null)
  // Optimistic profile-picture hint (avatar URL from the `pc_pfp` cookie) so a returning
  // user's avatar renders immediately instead of popping in from the initials/icon fallback
  // once their data loads. DISPLAY-ONLY. Starts null (hydration-safe), reconciled on mount.
  const [pfpHint, setPfpHint] = useState(null)
  const [subscription, setSubscription] = useState(null)
  const [nsfw, setNsfwState] = useState(false)
  const [gems, setGems] = useState(0)
  const [lastClaimTime] = useState(null)
  const [banned, setBanned] = useState(false)
  // v2's moderation ladder (ai_app_backend #1681): null while it is switched off, which shows today's site.
  const [moderation, setModeration] = useState(null)
  const [disputeBanned, setDisputeBanned] = useState(false)
  const [centroDecline, setCentroDecline] = useState(false)
  // v2's word on the declined-payment banner, for a visitor room 2's switch puts on v2 (src/lib/api/billing.ts,
  // `declinedNotice`); undefined for everyone else, who keep v1's `centro_decline`. Loaded only in a build with v2's
  // address, so no other page load carries it.
  const [v2Declined, setV2Declined] = useState(undefined)
  useEffect(() => {
    setV2Declined(undefined)
    if (!user || !process.env.NEXT_PUBLIC_AI_APP_API) return
    let live = true
    ;(async () => {
      try {
        const { declinedNotice } = await import('../lib/api/billing.ts')
        const response = await declinedNotice(await user.getIdToken())
        if (!response?.ok) return
        const { declined } = await response.json()
        if (live) setV2Declined(Boolean(declined))
      } catch {
        // v1's flag stands.
      }
    })()
    return () => {
      live = false
    }
  }, [user])
  const [crUser, setCrUser] = useState(false)
  const [userDataLoaded, setUserDataLoaded] = useState(false)
  const [accountCompletionPending, setAccountCompletionPending] =
    useState(false)
  const accountCompletionRef = useRef(false)
  const latestAccountCompletion = useRef(null)
  const firebaseUserRef = useRef(null)
  const accountDataRevision = useRef(0)
  const [accountDocument, setAccountDocument] = useState({
    uid: null,
    status: 'unknown',
  })

  // Firebase can announce a sign-in before create_account commits the Firestore
  // document. Invalidate any older data request before starting that write.
  const beginAccountCompletion = useCallback(() => {
    const attempt = Symbol('account-completion')
    accountCompletionRef.current = attempt
    latestAccountCompletion.current = attempt
    accountDataRevision.current += 1
    setAccountCompletionPending(true)
    return attempt
  }, [])

  const cancelAccountCompletion = useCallback((attempt) => {
    if (!attempt || accountCompletionRef.current !== attempt) return
    accountCompletionRef.current = false
    accountDataRevision.current += 1
    setAccountCompletionPending(false)
  }, [])

  const [deleted, setDeleted] = useState(false)
  const [deletionScheduledAt, setDeletionScheduledAt] = useState(null)
  const [linksShared, setLinksShared] = useState(null)
  const [safe, setSafe] = useState(null)

  const [newUser, setNewUser] = useState(false)
  const [tier, setTier] = useState(false)
  const [, setIsInitialData] = useState(false)
  const [country] = useState('')
  // Signup A/B on which mode a conversation starts in ("B" = plain chat/texting,
  // anything else = story/roleplay). Assigned in create_account and returned by
  // get_user_info; null for signed-out visitors and every pre-test account.
  const [chatModeAb, setChatModeAb] = useState(null)
  // Free chat messages left in the current 3-hour window, per model code, from
  // get_user_info on load and from every chat reply after. null until known.
  const [chatAllowance, setChatAllowance] = useState(null)
  // Has this account ever paid for a subscription. Derived server-side from the user
  // document's subscription_method, which survives churn — so it is true for a lapsed
  // subscriber sitting on Free. The upgrade page shows its annual saving line to these
  // accounts only.
  const [hasSubscribed, setHasSubscribed] = useState(false)
  const [ageGate, setAgeGate] = useState(null)
  const [ageGateModalOpen, setAgeGateModalOpen] = useState(null)
  // v2's `safe_only_country`: 'new', 'kept' or null (ai_app_backend #1427; isSafeCountry() reads its cookie).
  const [safeOnly, setSafeOnly] = useState(null)

  const [username, setUsername] = useState(null)
  const [profilePicture, setProfilePicture] = useState(null)
  const [email, setEmail] = useState(null)
  const [plan, setPlan] = useState(null)
  // The user's self-defined chat persona ("My Persona"), or null if never set.
  const [persona, setPersona] = useState(null)

  useEffect(() => {
    const setRoleCookie = async () => {
      try {
        if (user?.uid === process.env.NEXT_PUBLIC_ADMIN_UID) {
          document.cookie = `role=admin; Path=/; Max-Age=${7 * 24 * 60 * 60}`
        } else if (safeCountriesWhitelist.includes(user?.uid)) {
          document.cookie = `role=whitelisted; Path=/; Max-Age=${
            7 * 24 * 60 * 60
          }`
        }
      } catch (e) {
        console.error('Error setting role cookie', e)
      }
    }
    setRoleCookie()
  }, [user])

  // Seed the optimistic hint from the cookie once, after mount (client only) — keeping it out
  // of initial state avoids a hydration mismatch on statically-rendered pages.
  useEffect(() => {
    setAuthHint(!!Cookies.get('pc_authed'))
    setPlanHint(Cookies.get('pc_plan') ?? null)
    setPfpHint(Cookies.get('pc_pfp') ?? null)
  }, [])

  const handleLogout = async () => {
    // Firebase is lazy-loaded (see the auth-listener effect) so it stays out of the initial
    // bundle; pull it in on demand here.
    const [{ signOut }, { auth }] = await Promise.all([
      import('firebase/auth'),
      import('../config/firebase'),
    ])
    await signOut(auth)
    setAuthHint(false)
    setPlanHint(null)
    setPfpHint(null)
    Cookies.remove('pc_authed')
    Cookies.remove('pc_plan')
    Cookies.remove('pc_pfp')
    rememberSafeOnly(null)
    if (typeof window !== 'undefined') window.location.reload()
  }

  const handleRestoreAccount = async () => {
    try {
      if (!user) return
      const idToken = await user.getIdToken()
      const response = await accountsApi.restoreUser(idToken)
      if (response.ok) {
        setDeleted(false)
        setDeletionScheduledAt(null)
        if (typeof window !== 'undefined') window.location.reload()
      } else if (response.status === 404 || response.status === 410) {
        // Grace period lapsed / account already purged — genuinely unrecoverable.
        handleLogout()
      } else {
        // Transient (e.g. 5xx) — keep them on the restore screen so they can retry.
        console.error('Restore failed, status:', response.status)
        alert('Could not restore your account right now. Please try again.')
      }
    } catch (error) {
      // Network error — let them retry rather than logging out.
      console.error('Restore account failed:', error)
      alert('Could not restore your account right now. Please try again.')
    }
  }

  // Self-serve erasure from the banned overlay. A banned user has no other way
  // in (Settings is behind the block), so this is their GDPR path. Same
  // /api/delete_user soft-delete + 30d grace as Settings; confirm first since a
  // banned user can't reach the in-app restore screen to undo a misclick.
  const [deletingBannedAccount, setDeletingBannedAccount] = useState(false)

  const handleDeleteBannedAccount = async () => {
    if (!user || deletingBannedAccount) return
    if (
      typeof window !== 'undefined' &&
      !window.confirm(
        'Delete your account and all associated data? This cannot be undone.',
      )
    ) {
      return
    }
    setDeletingBannedAccount(true)
    try {
      const idToken = await user.getIdToken()
      const response = await accountsApi.deleteUser(idToken)
      if (response.ok) {
        handleLogout()
      } else {
        console.error('Delete account failed, status:', response.status)
        // v2 refuses while a plan may still charge, with words to show as given; anything else keeps the generic text.
        alert(
          (await refusalWords(response, 409, 'plan_active')) ||
            'Could not delete your account right now. Please try again.',
        )
        setDeletingBannedAccount(false)
      }
    } catch (error) {
      console.error('Delete account failed:', error)
      alert('Could not delete your account right now. Please try again.')
      setDeletingBannedAccount(false)
    }
  }

  // NSFW display is force-disabled: this ignores `newNsfw` and pins the state to false, as do
  // the useState above and the reset in the mount effect. That is intentional and dates to the
  // initial commit — the consumer toggle (AnimeLanding's NSFW switch) therefore renders but can
  // never turn on.
  //
  // This used to end with `if (typeof localstorage === 'undefined') return` followed by a
  // localStorage write. `localstorage` (lowercase) is not a real global, so that guard was
  // always true and the write was unreachable; nothing ever read the 'nsfw' key back either.
  // Removed rather than "corrected" — repairing the typo would start persisting a value the app
  // ignores, and un-pinning the state is a product decision, not a cleanup.
  const setNsfw = () => {
    setNsfwState(false)
  }

  useEffect(() => {
    setNsfwState(false)
    // safeGetItem guards the storage access — a bare localStorage read throws on iOS Safari
    // with cookies/storage blocked, which would strand this provider's mount.
    setVisitorId(safeGetItem('visitorId'))
  }, [])

  const setUserData = useCallback((userData) => {
    setIsInitialData(userData?.isDefaultData ?? false)

    setNewUser(userData?.safe ?? false)
    setDisputeBanned(userData?.chargeback)
    setTier(userData?.tier ?? 'A')
    setCentroDecline(userData?.centro_decline ?? false)
    setCrUser(userData?.cr_user ?? false)
    setChatModeAb(userData?.chat_mode_ab ?? null)
    setChatAllowance(userData?.chat_allowance_remaining ?? null)
    setHasSubscribed(userData?.has_subscribed ?? false)

    setAgeGate(userData?.ageGate ?? false)
    setSafeOnly(userData?.safeOnly ?? null)
    rememberSafeOnly(userData?.safeOnly ?? null)

    setUsername(userData?.username ?? null)
    const nextProfilePicture = userData?.profile_picture ?? null
    setProfilePicture(nextProfilePicture)
    // Remember the avatar (DISPLAY ONLY) so the next visit renders it optimistically.
    setPfpHint(nextProfilePicture)
    if (nextProfilePicture) {
      Cookies.set('pc_pfp', nextProfilePicture, {
        expires: 365,
        sameSite: 'lax',
      })
    } else {
      Cookies.remove('pc_pfp')
    }
    setEmail(userData?.email ?? null)
    setPlan(userData?.plan ?? null)
    setPersona(userData?.persona ?? null)

    const frontendData = userData?.frontend_data

    setLinksShared(frontendData?.links_shared ?? 0)
    setBanned(frontendData?.banned ?? false)
    setModeration(frontendData?.moderation ?? null)
    setDeleted(frontendData?.account_deleted ?? false)
    setDeletionScheduledAt(frontendData?.deletion_scheduled_at ?? null)
    setGems(frontendData?.gems ?? 0)
    setSafe(frontendData?.safe_mode ?? true)
  }, [])

  const finishAccountCompletion = useCallback(
    (result, attempt, uid) => {
      // A dismissed form may finish its server write in the background. Apply
      // that durable data only if this is still the same authenticated account
      // and a newer sign-in has not superseded the attempt.
      if (
        latestAccountCompletion.current !== attempt ||
        firebaseUserRef.current?.uid !== uid
      )
        return false
      accountDataRevision.current += 1
      if (result.initialUserData) setUserData(result.initialUserData)
      accountCompletionRef.current = false
      setAccountCompletionPending(false)
      setAccountDocument({ uid, status: 'ready' })
      // The account now exists even if a separate subscription request is delayed.
      setUserDataLoaded(true)
      return true
    },
    [setUserData],
  )

  const retryAccountData = useCallback(async () => {
    const currentUser = firebaseUserRef.current
    if (!currentUser || accountCompletionRef.current) return
    const revision = ++accountDataRevision.current
    const idToken = await currentUser.getIdToken()
    const { data, status } = await fetchAccountDocument({
      read: (signal) => accountsApi.getUserInfo(idToken, signal),
    })
    if (
      firebaseUserRef.current?.uid !== currentUser.uid ||
      revision !== accountDataRevision.current ||
      accountCompletionRef.current
    )
      return
    setAccountDocument({ uid: currentUser.uid, status })
    setUserData(data)
    setUserDataLoaded(true)
  }, [setUserData])

  const loadSubscriptionData = useCallback(
    async (uid, idToken) => {
      if (subscription || !user || accountCompletionRef.current) return
      const revision = accountDataRevision.current
      try {
        setUserDataLoaded(false)
        let userInfoStatus

        const fetchData = async (label, request) => {
          try {
            const response = await request()
            if (label === 'get_user_info') userInfoStatus = response.status
            if (!response.ok) return null
            return await response.json()
          } catch (error) {
            console.error(`${label} failed:`, error)
            return null
          }
        }

        const deviceId = getOrCreateDeviceId()

        const [subscriptionStatus, userData] = await Promise.all([
          fetchData('check_subscription_status', () =>
            accountsApi.checkSubscriptionStatus(idToken, deviceId),
          ),
          fetchData('get_user_info', () => accountsApi.getUserInfo(idToken)),
        ])

        if (
          revision !== accountDataRevision.current ||
          accountCompletionRef.current
        )
          return
        setAccountDocument({
          uid,
          status: accountDocumentStatus(userData, userInfoStatus),
        })
        setUserData(userData)
        setUserDataLoaded(true)

        // subscription logic...
        if (
          subscriptionStatus?.status === 'active' ||
          subscriptionStatus?.status === 'active (cancelling)'
        ) {
          setSubscription({
            id: uid,
            status: subscriptionStatus.status,
            plan: subscriptionStatus.plan,
            source: subscriptionStatus.source,
            subPlan: subscriptionStatus.subPlan,
          })
          // Cache the (server-validated) plan NAME so the next visit can render the paid
          // header/badge optimistically. DISPLAY-ONLY hint — never read for access/gating.
          setPlanHint(subscriptionStatus.plan)
          Cookies.set('pc_plan', subscriptionStatus.plan, {
            expires: 365,
            sameSite: 'lax',
          })
        } else if (subscriptionStatus) {
          // DEFINITIVE server response (non-null) that isn't active → genuinely Free.
          setSubscription({
            id: uid,
            status: 'inactive',
            plan: 'Free',
            source: null,
            subPlan: subscriptionStatus?.subPlan,
          })
          // Overwrite any stale paid hint the moment the server says Free (e.g. after churn),
          // so a downgraded user's next load hints Free instead of a phantom paid badge.
          setPlanHint('Free')
          Cookies.set('pc_plan', 'Free', { expires: 365, sameSite: 'lax' })
        }
        // else: subscriptionStatus is null → the check FAILED (network/5xx), NOT a real
        // downgrade. Do not persist 'Free' — that would flash the free UI to a paying user on
        // their next visit. Leave subscription/planHint untouched; a later successful load
        // reconciles, and the backend enforces real access regardless.
      } catch (e) {
        console.error('loadSubscriptionData error', e)
        setUserDataLoaded(true) // or false, depending on how you want to treat failures
      }
    },
    [subscription, user, setUserData],
  )

  useEffect(() => {
    // Firebase (firebase/auth + firebase/app via ../config/firebase) is dynamically
    // imported here instead of at module scope, so it drops out of the initial/shared bundle that
    // every anonymous landing-page visitor downloads. The optimistic cookie hints (authHint/
    // planHint/pfpHint) already render the header without waiting on Firebase, so deferring the
    // auth listener by one microtask has no visible effect — the UI never blocked on it.
    let unsubscribeAuth = () => {}
    let cancelled = false
    // Safety net for the iOS Safari deadlock: if Firebase's persistence read STALLS (a blocked or
    // locked IndexedDB can hang rather than throw), onAuthStateChanged never fires, the `.catch`
    // below never runs (the import succeeded), and `loading` stays true forever — every auth-gated
    // route is stranded on the full-screen spinner. Release the loader after 8s so the app renders
    // its (optimistic/logged-out) shell, which is recoverable. If the callback later fires, the
    // real auth state still applies.
    const bootstrapTimeout = setTimeout(() => {
      if (!cancelled) {
        console.warn('auth bootstrap timed out; releasing loader')
        setLoading(false)
      }
    }, 8000)
    ;(async () => {
      const [{ onAuthStateChanged }, { auth }] = await Promise.all([
        import('firebase/auth'),
        import('../config/firebase'),
      ])
      if (cancelled) return
      unsubscribeAuth = onAuthStateChanged(auth, async (user) => {
        clearTimeout(bootstrapTimeout)
        firebaseUserRef.current = user
        setAuthResolved(true)
        if (user) {
          setLoading(false) // Uncomment this for optimisation
          setUser(user)
          // Remember (client-side only) that this visitor is authed, so the next visit can
          // render the authed header optimistically without waiting for Firebase.
          setAuthHint(true)
          Cookies.set('pc_authed', '1', { expires: 365, sameSite: 'lax' })
          syncAuthedAttribute(true)
          try {
            const idToken = await user.getIdToken(true)
            await loadSubscriptionData(user.uid, idToken)
          } catch (err) {
            // A token refresh or data-load failure must not strand a fresh sign-in on the
            // full-screen loader (blocking = user && !userDataLoaded && !planHint). Release it;
            // the header falls back to its optimistic/empty state and reconciles on reload.
            console.error('auth token / subscription load failed', err)
            setUserDataLoaded(true)
          }
        } else {
          setUser(null)
          setSubscription(null)
          setAccountDocument({ uid: null, status: 'unknown' })
          // Default unauthenticated visitors to safe mode so NSFW content stays
          // blurred (matches Explore's safe-mode overlay everywhere Card is used).
          setSafe(true)
          setLoading(false)
          setAuthHint(false)
          setPlanHint(null)
          setPfpHint(null)
          Cookies.remove('pc_authed')
          Cookies.remove('pc_plan')
          Cookies.remove('pc_pfp')
          rememberSafeOnly(null)
          syncAuthedAttribute(false)
        }
      })
    })().catch((err) => {
      // The Firebase chunk failed to load (stale chunk hash after a deploy, network blip, an
      // aggressive blocker). Without this, onAuthStateChanged never registers, `loading` stays
      // true, and every auth-gated route is stranded behind the full-screen loader. Release it so
      // children render (unauthenticated view); a reload recovers once the chunk is reachable.
      console.error('firebase auth failed to load', err)
      if (!cancelled) {
        // Terminal path: the listener never registered, so auth can never resolve this
        // session. Mark it resolved (signed out) so the `authResolved && !user` redirect
        // gates still fire — matching the old `!loading && !user` behavior — instead of
        // leaving gated pages (gems, settings) as dead shells with no redirect.
        setAuthResolved(true)
        setLoading(false)
      }
    })

    return () => {
      cancelled = true
      clearTimeout(bootstrapTimeout)
      unsubscribeAuth()
    }
    // These dependency arrays are causing the data to be fetched 3 times instead of just 1
  }, [loadSubscriptionData, accountCompletionPending])

  // Effective plan for the CLIENT experience — UI *and* client-side tier gating. Prefer the
  // real subscription; fall back to the optimistic cookie hint ONLY while subscription is still
  // loading (`!userDataLoaded`). The instant real data arrives, `subscription.plan` takes over
  // and any stale hint is ignored, so it self-corrects. The cookie is forgeable, but the
  // backend independently enforces every gated action (the API is the real boundary), so this
  // driving a client gate unlocks nothing an attacker couldn't already request directly — it
  // only decides what the CLIENT attempts/shows. Gem SPENDING still reads the real `gems`
  // balance (see below) so we never optimistically start an action the user can't afford.
  const displayPlan = subscription?.plan ?? (!userDataLoaded ? planHint : null)

  // Tier ladder computed ONCE from the effective plan so every gate shares one optimistic
  // source of truth (a returning paid user isn't blocked during the auth-load window).
  // Max ($83.99, above Studio; plan-facts.js) is still a paid plan: until 6 October 2026 it
  // was missing here, so Max subscribers were gated as Free (S18; chatTier already ranks it).
  const isPlusOrHigher = ['Plus', 'Premium', 'Studio', 'Pro', 'Max'].includes(
    displayPlan,
  )
  const isPremiumOrHigher = ['Premium', 'Studio', 'Pro', 'Max'].includes(
    displayPlan,
  )
  const isStudioOrHigher = ['Studio', 'Pro', 'Max'].includes(displayPlan)

  // Avatar to use for DISPLAY ONLY. Prefer the real `profilePicture`; fall back to the cookie
  // hint ONLY while data is still loading, so a returning user's avatar shows immediately and
  // self-corrects (a user who removed their picture shows the fallback once loaded).
  const displayProfilePicture =
    profilePicture ?? (!userDataLoaded ? pfpHint : null)

  // Mode a character/group opens in before the user has ever touched the toggle.
  // Mirrors account.default_story_mode on the backend — both sides must resolve
  // the missing story_mode field the same way or the UI and the prompt disagree.
  const defaultStoryMode = chatModeAb !== 'B'

  const value = {
    // Keep signup forms mounted (including modal forms) until the Firestore
    // account is durable. Internal Firebase state still supplies the credential.
    user: accountCompletionPending ? null : user,
    loading,
    authResolved: authResolved && !accountCompletionPending,
    authHint: accountCompletionPending ? false : authHint,
    planHint,
    displayPlan,
    isPlusOrHigher,
    isPremiumOrHigher,
    isStudioOrHigher,
    subscription,
    nsfw,
    setNsfw,
    gems,
    setGems,
    lastClaimTime,
    linksShared,
    banned,
    moderation,
    safe,
    setSafe,
    newUser,
    disputeBanned,
    tier,
    accountCompletionPending,
    accountDocumentStatus:
      accountDocument.uid === user?.uid ? accountDocument.status : 'unknown',
    retryAccountData,
    beginAccountCompletion,
    finishAccountCompletion,
    cancelAccountCompletion,
    country,
    chatAllowance,
    setChatAllowance,
    hasSubscribed,
    defaultStoryMode,
    centroDecline: v2Declined ?? centroDecline,
    crUser,
    userDataLoaded,
    ageGate,
    ageGateModalOpen,
    setAgeGateModalOpen,
    safeOnly,

    username,
    profilePicture,
    displayProfilePicture,
    email,
    plan,
    persona,
    setPersona,
  }

  const deletionDate = deletionScheduledAt
    ? new Date(deletionScheduledAt)
    : null
  const isPendingDeletion =
    deleted &&
    deletionDate &&
    !isNaN(deletionDate.getTime()) &&
    deletionDate.getTime() > Date.now()

  return (
    <AuthContext.Provider value={value}>
      {isPendingDeletion ? (
        <div className="flex flex-col items-center justify-center min-h-screen text-center text-white px-4">
          <h1 className="text-2xl font-semibold mt-20 mb-6 text-[var(--main-bg-color)]">
            Your Account Is Scheduled for Deletion
          </h1>
          <p className="mb-4">
            This account is disabled and all of its data will be permanently
            deleted on{' '}
            <strong>
              {deletionDate.toLocaleDateString(undefined, {
                year: 'numeric',
                month: 'long',
                day: 'numeric',
              })}
            </strong>
            .
          </p>
          <p className="mb-8">
            You can restore your account and cancel the deletion any time before
            then.
          </p>
          <button
            className="px-8 py-2 bg-[var(--foreground-bg-color)] hover:bg-[var(--secondary-bg-color)] text-white rounded-full focus:outline-none font-semibold"
            onClick={handleRestoreAccount}
          >
            Restore My Account
          </button>
          <button className="text-white underline mt-6" onClick={handleLogout}>
            Log Out
          </button>
        </div>
      ) : deleted ? (
        <div className="flex flex-col items-center justify-center min-h-screen text-center text-white px-4">
          <h1 className="text-2xl font-semibold mt-20 mb-6 text-[var(--main-bg-color)]">
            This Promptchan Account Has Been Deleted
          </h1>
          <p className="mb-4">
            The account associated with this email has been permanently deleted
            and cannot be restored.
          </p>
          <p className="mb-8">
            To continue using our services, please log in with a different email
            address or SSO provider.
          </p>
          <a
            className="text-white underline mr-2"
            href="https://centrohelp.eu"
            target="_blank"
            rel="noopener noreferrer"
          >
            Subscribed? Cancel Plan
          </a>
          <a
            className="text-white underline mr-2"
            href="mailto:support@promptchan.com"
          >
            Contact Us
          </a>
          <button
            className=" px-8 py-2 mt-8 bg-[var(--foreground-bg-color)] hover:bg-[var(--secondary-bg-color)] text-white rounded-full focus:outline-none font-semibold"
            onClick={handleLogout}
          >
            Go Back to Login
          </button>
        </div>
      ) : banned ? (
        <div className="text-center">
          <h1 className="text-white  mt-20">
            Your account has been banned for breaking the terms of service. As a
            result of these actions, you will no longer be able to use our
            platform. Please contact support if you believe this is a mistake.
          </h1>
          <a
            className="text-white underline mr-2"
            href="https://centrohelp.eu"
            target="_blank"
            rel="noopener noreferrer"
          >
            Subscribed? Cancel Plan
          </a>
          <a
            className="text-white underline mr-2"
            href="mailto:support@promptchan.com"
          >
            Contact Us
          </a>

          <button className="text-white underline" onClick={handleLogout}>
            Go back
          </button>

          <div className="mt-10">
            <button
              className="text-gray-400 underline text-sm disabled:opacity-50"
              onClick={handleDeleteBannedAccount}
              disabled={deletingBannedAccount}
            >
              {deletingBannedAccount
                ? 'Deleting…'
                : 'Delete my account and data'}
            </button>
          </div>
        </div>
      ) : disputeBanned ? (
        <div className="text-center">
          <h1 className="text-white  mt-20">
            Your account has been restricted and is currently under
            investigation due to suspicious activities associated with
            fraudulent chargebacks. As a result of these actions, you will no
            longer be able to use our platform. Please contact support if you
            believe this is a mistake.
          </h1>
          <a
            className="text-white underline mr-2"
            href="https://centrohelp.eu"
            target="_blank"
            rel="noopener noreferrer"
          >
            Subscribed? Cancel Plan
          </a>
          <a
            className="text-white underline mr-2"
            href="mailto:support@promptchan.com"
          >
            Contact Us
          </a>
          <button className="text-white underline" onClick={handleLogout}>
            Go back
          </button>
        </div>
      ) : (
        children
      )}
    </AuthContext.Provider>
  )
}
